<template>

  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-10 col-xl-9">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0">Orden de Servicio</p>
              <argon-button
                v-if="isEdit"
                color="secondary"
                size="sm"
                class="ms-auto me-2"
                @click="sharePublicPdf"
                title="Compartir enlace público PDF"
              >
                <i class="fas fa-share-alt"></i>
              </argon-button>
            </div>
            <small
              v-for="notice in serviceOrderNotices"
              :key="notice"
              class="text-muted d-block mt-1"
            >
              <i class="fas fa-exclamation-circle text-warning me-1"></i>{{ notice }}
            </small>
          </div>
          <div class="card-body">
            <form autocomplete="off" @submit.prevent>
              <!-- Sección 1: Datos del Cliente -->
              <p class="text-uppercase text-sm">Datos del Cliente</p>
              <div class="row">
                <div class="col-md-12">
                  <label class="form-control-label text-sm">Cliente</label>
                  <div class="d-flex gap-2">
                    <select
                      ref="clientSelect"
                      class="form-select flex-grow-1"
                      v-model="form.customer_id"
                      @change="selectClient"
                    >
                      <option value="">Seleccione cliente</option>
                      <option v-for="client in clients" :key="client.id" :value="client.id">
                        {{ client.document }} - {{ client.name }}
                      </option>
                    </select>
                    <argon-button
                      color="success"
                      size="sm"
                      @click="openAddClientModal"
                      title="Agregar cliente"
                    >
                      <i class="fas fa-user-plus"></i>
                    </argon-button>
                  </div>
                </div>
                <div class="col-md-8">
                  <label class="form-control-label">Nombre del Cliente</label>
                  <input type="text" v-model="form.customer_name" class="form-control" readonly />
                </div>
                <div class="col-md-4">
                  <label class="form-control-label">Teléfono</label>
                  <input type="text" v-model="form.phone" class="form-control" readonly />
                </div>
              </div>

              <!-- Sección 2: Datos de la Moto -->
              <p class="text-uppercase text-sm mt-4">Datos de la Moto</p>
              <div class="row">
                <div class="col-md-6 col-lg-3 position-relative">
                  <label class="form-control-label">Marca de la Moto</label>
                  <input
                    type="text"
                    v-model="form.motorcycle_brand"
                    class="form-control"
                    placeholder="Escriba o seleccione una marca"
                    autocomplete="off"
                    @input="onBrandInput"
                    @focus="showBrandSuggestions = true"
                    @blur="hideBrandSuggestionsDelayed"
                  />
                  <div
                    v-if="showBrandSuggestions && filteredBrandSuggestions.length"
                    class="brand-suggestions"
                  >
                    <button
                      v-for="brand in filteredBrandSuggestions"
                      :key="brand"
                      type="button"
                      class="brand-suggestion-item"
                      @mousedown.prevent="selectBrandSuggestion(brand)"
                    >
                      {{ brand }}
                    </button>
                  </div>
                </div>
                <div class="col-md-6 col-lg-3 position-relative">
                  <label class="form-control-label">Modelo de la Moto</label>
                  <input
                    type="text"
                    v-model="form.motorcycle_model"
                    class="form-control"
                    placeholder="Escriba o seleccione año / modelo"
                    autocomplete="off"
                    @input="onModelInput"
                    @focus="showModelSuggestions = true"
                    @blur="hideModelSuggestionsDelayed"
                  />
                  <div
                    v-if="showModelSuggestions && filteredModelSuggestions.length"
                    class="brand-suggestions"
                  >
                    <button
                      v-for="model in filteredModelSuggestions"
                      :key="model"
                      type="button"
                      class="brand-suggestion-item"
                      @mousedown.prevent="selectModelSuggestion(model)"
                    >
                      {{ model }}
                    </button>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <label class="form-control-label">Color de la Moto</label>
                  <argon-input type="text" v-model="form.motorcycle_color" />
                </div>
                <div class="col-md-4 col-lg-3">
                  <label class="form-control-label">Placa</label>
                  <argon-input type="text" v-model="form.license_plate" />
                </div>
                <div class="col-md-4 col-lg-3">
                  <label class="form-control-label">Kilometraje</label>
                  <argon-input type="text" v-model="form.mileage" />
                </div>
                
                <div class="col-md-4 col-12">
                  <label class="form-control-label">Técnico Asignado</label>
                  <select
                    ref="technicianSelect"
                    class="form-select"
                    v-model="form.user_id"
                  >
                    <option value="">Seleccione técnico</option>
                    <option v-for="tech in technicians" :key="tech.id" :value="String(tech.id)">
                      {{ formatTechnicianLabel(tech) }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4 col-12">
                  <label class="form-control-label">Fecha y Hora de Entrada</label>
                  <input
                    v-model="form.entry_at"
                    type="datetime-local"
                    class="form-control"
                  />
                </div>
                <div class="col-12 mt-3 d-flex align-items-end">
                  <div class="form-check form-switch d-flex align-items-center gap-2 doc-check-wrap">
                    <input
                      id="documents_up_to_date"
                      v-model="form.documents_up_to_date"
                      class="form-check-input"
                      type="checkbox"
                    />
                    <label class="form-check-label mb-0 doc-check-label" for="documents_up_to_date">
                      Documentos al día
                    </label>
                  </div>
                </div>
              </div>

              <!-- Sección 3: Fotos y Observaciones -->
              <p class="text-uppercase text-sm mt-4">Fotos y Observaciones</p>
              <div class="row">
                <div class="col-md-12">
                  <label class="form-control-label d-flex justify-content-between align-items-center">
                    <span>Fotos de la Moto</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="uploadingMotorcyclePhoto"
                      @click="$refs.photoInput.click()"
                    >
                      <i class="fa fa-camera me-1"></i>
                      {{ uploadingMotorcyclePhoto ? "Subiendo..." : "Tomar / subir fotos" }}
                    </button>
                  </label>
                  <div class="border rounded p-3">
                    <div
                      v-if="motorcyclePhotos.length"
                      class="row g-2"
                    >
                      <div
                        v-for="(photo, index) in motorcyclePhotos"
                        :key="photo.id || photo.preview || index"
                        class="col-6 col-md-4 col-lg-3 d-flex justify-content-center"
                      >
                        <div class="photo-card position-relative">
                          <img
                            :src="photoPreview(photo)"
                            alt="Moto"
                            class="photo-thumb"
                          />
                          <button
                            type="button"
                            class="photo-remove-btn"
                            @click="removePhoto(index)"
                            title="Eliminar foto"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted">
                      No hay fotos cargadas.
                    </div>
                    <input
                      ref="photoInput"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      class="d-none"
                      @change="onPhotoChange"
                    />
                  </div>
                </div>
                <div class="col-md-12 mt-2">
                  <label class="form-control-label d-flex justify-content-between align-items-center">
                    <span>Video de evidencia <small class="text-muted">(opcional)</small></span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="uploadingEvidenceVideo"
                      @click="$refs.evidenceVideoInput.click()"
                    >
                      <i class="fa fa-video me-1"></i>
                      {{ uploadingEvidenceVideo ? 'Subiendo...' : 'Subir video' }}
                    </button>
                  </label>
                  <div class="border rounded p-3">
                    <div v-if="evidenceVideos.length" class="d-flex flex-column gap-3">
                      <div
                        v-for="video in evidenceVideos"
                        :key="video.id"
                        class="evidence-video-card position-relative"
                      >
                        <video
                          :src="mediaUrl(video.file_path)"
                          class="evidence-video-player"
                          controls
                          preload="metadata"
                        ></video>
                        <p class="text-xs text-muted mb-0 mt-2 text-truncate" :title="video.file_name">
                          {{ video.file_name }}
                        </p>
                        <button
                          type="button"
                          class="photo-remove-btn"
                          @click="removeEvidenceVideo(video.id)"
                          title="Eliminar video"
                        >
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted">
                      No hay video de evidencia cargado.
                    </div>
                    <input
                      ref="evidenceVideoInput"
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/ogg,video/x-m4v,.mp4,.webm,.mov,.ogg,.m4v"
                      class="d-none"
                      @change="onEvidenceVideoChange"
                    />
                  </div>
                </div>
                <div class="col-md-12">
                  <label class="form-control-label">Observaciones</label>
                  <div class="observations-voice-wrap position-relative">
                    <textarea
                      ref="observationsTextarea"
                      v-model="form.observations"
                      class="form-control observations-textarea"
                      rows="6"
                      placeholder="Escriba las observaciones o use el micrófono"
                    ></textarea>
                    <button
                      type="button"
                      class="observations-voice-btn"
                      :class="{ 'observations-voice-btn--active': observationsListening }"
                      :title="observationsListening ? 'Detener dictado' : 'Dictar con el micrófono'"
                      :aria-label="observationsListening ? 'Detener dictado' : 'Dictar con el micrófono'"
                      @click="toggleObservationsVoice"
                    >
                      <i class="fas" :class="observationsListening ? 'fa-stop' : 'fa-microphone'"></i>
                    </button>
                  </div>
                  <small v-if="observationsListening" class="text-danger d-block mt-1">
                    <i class="fas fa-circle text-danger" style="font-size: 0.5rem;"></i>
                    Escuchando… haga clic en el micrófono para detener.
                  </small>
                  <small v-else-if="!speechRecognitionSupported" class="text-muted d-block mt-1">
                    El dictado por voz no está disponible en este navegador.
                  </small>
                </div>
              </div>

              <!-- Servicios manuales (opcionales) -->
              <div class="d-flex align-items-center mt-4 mb-2">
                <p class="text-uppercase text-sm mb-0">Servicios (opcional)</p>
                <button
                  type="button"
                  class="btn btn-outline-success btn-sm mb-0 ms-auto"
                  @click="addService"
                >
                  <i class="fas fa-plus me-1"></i> Agregar servicio
                </button>
              </div>
              <p v-if="!form.services.length" class="text-xs text-secondary mb-0">
                No hay servicios agregados. Use «Agregar servicio» para asociar servicios y su costo a la orden.
              </p>
              <div
                v-for="(service, index) in form.services"
                :key="service.uid"
                class="row g-2 align-items-end mb-2"
              >
                <div class="col-7 col-md-8 position-relative">
                  <label v-if="index === 0" class="form-control-label">Servicio</label>
                  <input
                    v-model="service.name"
                    type="text"
                    class="form-control"
                    placeholder="Ej: Cambio de aceite"
                    autocomplete="off"
                    @focus="activeServiceIndex = index"
                    @input="activeServiceIndex = index"
                    @blur="hideServiceSuggestionsDelayed"
                  />
                  <div
                    v-if="activeServiceIndex === index && filteredServiceSuggestions(service.name).length"
                    class="brand-suggestions"
                  >
                    <button
                      v-for="suggestion in filteredServiceSuggestions(service.name)"
                      :key="suggestion.name"
                      type="button"
                      class="brand-suggestion-item d-flex justify-content-between"
                      @mousedown.prevent="selectServiceSuggestion(service, suggestion)"
                    >
                      <span>{{ suggestion.name }}</span>
                      <span class="text-secondary">${{ formatMoney(suggestion.price) }}</span>
                    </button>
                  </div>
                </div>
                <div class="col-4 col-md-3">
                  <label v-if="index === 0" class="form-control-label">Valor</label>
                  <input
                    v-model.number="service.price"
                    type="number"
                    min="0"
                    step="any"
                    class="form-control"
                    placeholder="0"
                  />
                </div>
                <div class="col-1 text-end">
                  <button
                    type="button"
                    class="btn btn-link text-danger px-1 mb-1"
                    title="Quitar servicio"
                    aria-label="Quitar servicio"
                    @click="removeService(index)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <p v-if="form.services.length" class="text-end text-sm font-weight-bold mb-0">
                Total servicios: ${{ formatMoney(servicesTotal) }}
              </p>

              <!-- Sección 4: Inventario -->
              <p class="text-uppercase text-sm mt-4">Inventario de la Moto</p>
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ítem</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Bueno</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Regular</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Malo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="field in inventoryFields" :key="field.key">
                      <td class="text-sm">{{ field.label }}</td>
                      <td class="text-center">
                        <div class="form-check inventory-radio-wrap mb-0">
                          <input
                            class="form-check-input inventory-radio-input"
                            type="radio"
                            :id="`inventory-${field.key}-bueno`"
                            :name="`inventory-${field.key}`"
                            value="Bueno"
                            v-model="form[field.key]"
                          />
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="form-check inventory-radio-wrap mb-0">
                          <input
                            class="form-check-input inventory-radio-input"
                            type="radio"
                            :id="`inventory-${field.key}-regular`"
                            :name="`inventory-${field.key}`"
                            value="Regular"
                            v-model="form[field.key]"
                          />
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="form-check inventory-radio-wrap mb-0">
                          <input
                            class="form-check-input inventory-radio-input"
                            type="radio"
                            :id="`inventory-${field.key}-malo`"
                            :name="`inventory-${field.key}`"
                            value="Malo"
                            v-model="form[field.key]"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="row mt-3">
                <div
                  v-for="toggle in inventoryToggles"
                  :key="toggle.key"
                  class="col-4"
                >
                  <label class="form-control-label d-block mb-2">
                    {{ toggle.label }}
                  </label>
                  <div class="form-check form-switch">
                    <input
                      :id="toggle.key"
                      v-model="form[toggle.key]"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                    />
                    <label class="form-check-label" :for="toggle.key">
                      {{ form[toggle.key] ? "Sí" : "No" }}
                    </label>
                  </div>
                </div>
              </div>

              <p class="text-uppercase text-sm mt-4">Firma del Cliente</p>
              <div class="border rounded">
                <canvas
                  ref="signatureCanvas"
                  class="w-100"
                  style="height: 180px; display: block; touch-action: none"
                  @mousedown="startDraw"
                  @mousemove="draw"
                  @mouseup="endDraw"
                  @mouseleave="endDraw"
                  @touchstart.prevent="startDrawTouch"
                  @touchmove.prevent="drawTouch"
                  @touchend.prevent="endDraw"
                ></canvas>
              </div>

              <div class="row g-3 mt-4 align-items-end">
                <div class="col-md-4">
                  <label class="form-control-label">Estado</label>
                  <select v-model.number="form.status" class="form-select">
                    <option :value="0">Borrador</option>
                    <option :value="1">Formalizada</option>
                    <option :value="2">Rechazada</option>
                  </select>
                </div>
                <div class="col-md-8">
                  <div class="d-flex flex-wrap gap-2 justify-content-md-end">
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      @click="clearSignature"
                    >
                      Limpiar Firma
                    </button>
                    <button
                      v-if="isEdit"
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      title="Compartir enlace público PDF por WhatsApp"
                      @click="sharePublicPdf"
                    >
                      <i class="fas fa-share-alt"></i>
                    </button>
                    <button
                      v-if="isEdit"
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      @click="generatePdf"
                    >
                      <i class="fa fa-file-pdf me-1"></i> Generar PDF
                    </button>
                    <argon-button
                      color="success"
                      size="sm"
                      @click="submitForm"
                      :disabled="saving"
                    >
                      {{ saving ? "Guardando..." : "Guardar" }}
                    </argon-button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="ModalAddClientServiceOrder" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content card">
        <div class="modal-header">
          <h5 class="modal-title">Agregar Cliente</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-md-12">
              <label class="form-control-label">Nombre</label>
              <input v-model="newClient.name" type="text" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-control-label">Documento</label>
              <input v-model="newClient.document" type="text" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-control-label">Teléfono</label>
              <input v-model="newClient.phone" type="text" class="form-control" />
            </div>
            <div class="col-md-12">
              <label class="form-control-label">Correo</label>
              <input v-model="newClient.email" type="email" class="form-control" />
            </div>
            <div class="col-md-6">
              <div class="form-check form-switch mt-md-4">
                <input
                  id="new_client_oil_reminder_enabled"
                  v-model="newClient.oil_reminder_enabled"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                />
                <label class="form-check-label" for="new_client_oil_reminder_enabled">
                  Recordatorio de cambio de aceite
                </label>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-control-label">Recordatorio cada (días)</label>
              <input
                v-model="newClient.oil_reminder_days"
                type="number"
                min="1"
                max="365"
                class="form-control"
                :disabled="!newClient.oil_reminder_enabled"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-success btn-sm" @click="createClient">Guardar cliente</button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import $ from "jquery";
import "select2/dist/css/select2.css";
import "select2";
import ArgonInput from "../../../components/ArgonInput.vue";
import ArgonButton from "../../../components/ArgonButton.vue";
import userService from "../../../services/user.service";
import serviceOrdersService from "../../../services/serviceOrders.service";
import { sharePublicDocument } from "../../../helpers/whatsapp";
import {
  KNOWN_MOTORCYCLE_BRANDS,
  buildModelYearSuggestions,
  mergeUniqueSuggestions,
  filterSuggestions,
} from "../../../constants/motorcycleBrands";
import { compressImageFile, dataUrlToFile } from "../../../helpers/compressImage";

const MOTO_PHOTO_MEDIA_TYPE = "service_order_moto_photo";

const SERVICE_ORDER_NOTICES = [
  "En caso de desistir del servicio, se cobrará $30.000.",
  "Si la moto no se retira dentro de los 3 días siguientes a terminado el servicio, se cobrará parqueadero.",
];

const emptyForm = () => ({
  customer_id: "",
  customer_name: "",
  phone: "",
  motorcycle_brand: "",
  motorcycle_model: "",
  motorcycle_color: "",
  license_plate: "",
  mileage: "",
  entry_at: "",
  user_id: "",
  assigned_technician: "",
  documents_up_to_date: false,
  motorcycle_photo_path: null,
  observations: "",
  mirrors: "",
  side_panels: "",
  tires: "",
  headlight: "",
  fuel_level: "",
  engine_sound: "",
  electricity: "",
  fenders: "",
  turn_signals: "",
  tachometer: "",
  fork_boots: "",
  footpegs: "",
  has_tools: false,
  has_gps: false,
  has_wash: false,
  customer_signature: null,
  status: 0,
  services: [],
});

let serviceUid = 0;
const makeServiceRow = (name = "", price = null) => ({
  uid: ++serviceUid,
  name,
  price,
});

export default {
  name: "ServiceOrderForm",
  components: { ArgonInput, ArgonButton },
  data() {
    return {
      form: emptyForm(),
      saving: false,
      apiUrl: process.env.VUE_APP_API_IMAGE_URL || process.env.VUE_APP_API_BASE_URL,
      evidenceVideos: [],
      uploadingEvidenceVideo: false,
      uploadingMotorcyclePhoto: false,
      pendingMotorcyclePhotos: [],
      motorcyclePhotos: [],
      clients: [],
      technicians: [],
      registeredBrands: [],
      registeredModels: [],
      showBrandSuggestions: false,
      showModelSuggestions: false,
      brandSuggestionsTimer: null,
      modelSuggestionsTimer: null,
      addClientModal: null,
      serviceOrderNotices: SERVICE_ORDER_NOTICES,
      serviceSuggestions: [],
      activeServiceIndex: null,
      serviceSuggestionsTimer: null,
      newClient: {
        name: "",
        document: "",
        phone: "",
        email: "",
        oil_reminder_enabled: false,
        oil_reminder_days: 30,
      },
      drawing: false,
      inventoryFields: [
        { key: "mirrors", label: "Retrovisores" },
        { key: "side_panels", label: "Tapas Laterales" },
        { key: "tires", label: "Llantas" },
        { key: "headlight", label: "Farola" },
        { key: "fuel_level", label: "Nivel de Gasolina" },
        { key: "engine_sound", label: "Sonido del Motor" },
        { key: "electricity", label: "Electricidad" },
        { key: "fenders", label: "Guardabarros" },
        { key: "turn_signals", label: "Direccionales" },
        { key: "tachometer", label: "Tacómetro" },
        { key: "fork_boots", label: "Guarda Polvo" },
        { key: "footpegs", label: "Posapiés" },
      ],
      inventoryToggles: [
        { key: "has_tools", label: "Herramienta" },
        { key: "has_gps", label: "GPS" },
        { key: "has_wash", label: "Lavado" },
      ],
      observationsListening: false,
      speechRecognition: null,
      speechRecognitionSupported: false,
    };
  },
  computed: {
    orderId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    isEdit() {
      return Boolean(this.orderId);
    },
    currentYear() {
      return new Date().getFullYear();
    },
    allBrandSuggestions() {
      return mergeUniqueSuggestions(KNOWN_MOTORCYCLE_BRANDS, this.registeredBrands);
    },
    filteredBrandSuggestions() {
      return filterSuggestions(this.allBrandSuggestions, this.form.motorcycle_brand);
    },
    allModelSuggestions() {
      return mergeUniqueSuggestions(
        buildModelYearSuggestions(2000),
        this.registeredModels,
        this.form.motorcycle_model
      );
    },
    filteredModelSuggestions() {
      return filterSuggestions(this.allModelSuggestions, this.form.motorcycle_model);
    },
    servicesTotal() {
      return this.form.services.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
    },
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    },
  },
  watch: {
    "form.license_plate"(value) {
      const upper = (value || "").toUpperCase();
      if (upper !== value) {
        this.form.license_plate = upper;
      }
    },
    "form.motorcycle_brand"() {
      this.loadRegisteredModelsForBrand();
    },
  },
  async mounted() {
    if (!this.isEdit && !(await this.confirmServiceOrderNotices())) {
      this.$router.push("/service-orders");
      return;
    }
    this.initObservationsSpeech();
    this.initCanvas();
    window.addEventListener("resize", this.initCanvas);
    await this.getClients();
    await this.getTechnicians();
    await this.loadRegisteredBrands();
    this.loadServiceSuggestions();
    if (!this.form.entry_at) {
      this.form.entry_at = this.nowLocalDatetime();
    }
    if (this.isEdit) {
      await this.loadOrder();
    } else {
      this.$nextTick(async () => {
        this.initClientSelect2();
        this.applyDefaultTechnicianForCreate();
        await this.$nextTick();
        this.initTechnicianSelect2();
        this.$nextTick(() => {
          this.selectTechnicianInSelect2(this.form.user_id);
        });
      });
    }
  },
  beforeUnmount() {
    if (this.$refs.clientSelect) {
      const select = $(this.$refs.clientSelect);
      if (select.hasClass("select2-hidden-accessible")) {
        select.select2("destroy");
      }
    }
    if (this.brandSuggestionsTimer) {
      clearTimeout(this.brandSuggestionsTimer);
    }
    if (this.modelSuggestionsTimer) {
      clearTimeout(this.modelSuggestionsTimer);
    }
    if (this.$refs.technicianSelect) {
      const select = $(this.$refs.technicianSelect);
      if (select.hasClass("select2-hidden-accessible")) {
        select.select2("destroy");
      }
    }
    window.removeEventListener("resize", this.initCanvas);
    this.stopObservationsVoice();
  },
  methods: {
    ...mapActions("serviceOrders", ["getData", "save"]),
    initObservationsSpeech() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      this.speechRecognitionSupported = Boolean(SpeechRecognition);
      if (!SpeechRecognition) {
        return;
      }
      const recognition = new SpeechRecognition();
      recognition.lang = "es-CO";
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0]?.transcript || "";
          }
        }
        if (transcript.trim()) {
          const base = (this.form.observations || "").trim();
          const addition = transcript.trim();
          this.form.observations = base ? `${base} ${addition}` : addition;
        }
      };
      recognition.onend = () => {
        this.observationsListening = false;
      };
      recognition.onerror = (event) => {
        this.observationsListening = false;
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          Swal.fire(
            "Micrófono bloqueado",
            "Permita el acceso al micrófono en el navegador para dictar observaciones.",
            "warning"
          );
        } else if (event.error !== "aborted" && event.error !== "no-speech") {
          Swal.fire("Dictado", "No se pudo usar el micrófono. Intente de nuevo.", "warning");
        }
      };
      this.speechRecognition = recognition;
    },
    toggleObservationsVoice() {
      if (!this.speechRecognitionSupported) {
        Swal.fire(
          "No disponible",
          "Su navegador no soporta dictado por voz. Use Chrome o Edge.",
          "info"
        );
        return;
      }
      if (this.observationsListening) {
        this.stopObservationsVoice();
        return;
      }
      if (!this.speechRecognition) {
        this.initObservationsSpeech();
      }
      try {
        this.speechRecognition.start();
        this.observationsListening = true;
      } catch (error) {
        console.error("Speech recognition start:", error);
        Swal.fire("Dictado", "No se pudo iniciar el micrófono.", "warning");
      }
    },
    stopObservationsVoice() {
      if (this.speechRecognition && this.observationsListening) {
        try {
          this.speechRecognition.stop();
        } catch (error) {
          console.warn("Speech recognition stop:", error);
        }
      }
      this.observationsListening = false;
    },
    nowLocalDatetime() {
      const d = new Date();
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().slice(0, 16);
    },
    initCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 180;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#00d4ff";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    },
    getCtx() {
      return this.$refs.signatureCanvas.getContext("2d");
    },
    startDraw(e) {
      this.drawing = true;
      const ctx = this.getCtx();
      const { x, y } = this.pointer(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    draw(e) {
      if (!this.drawing) return;
      const ctx = this.getCtx();
      const { x, y } = this.pointer(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    endDraw() {
      this.drawing = false;
    },
    startDrawTouch(e) {
      this.drawing = true;
      const ctx = this.getCtx();
      const { x, y } = this.pointer(e.touches[0]);
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    drawTouch(e) {
      if (!this.drawing) return;
      const ctx = this.getCtx();
      const { x, y } = this.pointer(e.touches[0]);
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    pointer(e) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      };
    },
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.form.customer_signature = null;
    },
    async getClients() {
      try {
        const response = await userService.getAll("all");
        const allUsers = response.data.data || response.data || [];
        this.clients = allUsers.filter((user) => String(user.role) === "4");
        this.$nextTick(() => {
          this.initClientSelect2();
        });
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },
    async getTechnicians() {
      try {
        const response = await userService.getAll("all");
        const allUsers = response.data.data || response.data || [];
        this.technicians = allUsers.filter((user) => ["1", "6"].includes(String(user.role)));
      } catch (error) {
        console.error("Error loading technicians:", error);
      }
    },
    ensureTechnicianInList(tech) {
      if (!tech) return;
      const id = tech.id ?? tech;
      if (!id) return;
      const exists = this.technicians.some((t) => String(t.id) === String(id));
      if (exists) return;
      if (typeof tech === "object" && tech.name) {
        this.technicians = [...this.technicians, tech];
        return;
      }
      this.technicians = [
        ...this.technicians,
        {
          id,
          name: this.form.assigned_technician || `Usuario #${id}`,
        },
      ];
    },
    applyDefaultTechnicianForCreate() {
      const user = this.currentUser;
      if (!user?.id) return;
      this.ensureTechnicianInList(user);
      this.form.user_id = String(user.id);
      this.syncTechnicianFieldsFromUserId();
    },
    initClientSelect2() {
      if (!this.$refs.clientSelect) return;
      const select = $(this.$refs.clientSelect);
      if (select.hasClass("select2-hidden-accessible")) {
        select.select2("destroy");
      }
      select.off("change");
      select.select2({
        placeholder: "Seleccione cliente",
        allowClear: true,
        width: "100%",
      });
      select.on("change", (e) => {
        this.form.customer_id = e.target.value;
        this.selectClient();
      });
      if (this.form.customer_id) {
        select.val(String(this.form.customer_id)).trigger("change");
      }
    },
    async confirmServiceOrderNotices() {
      const items = SERVICE_ORDER_NOTICES.map((notice) => `<li class="mb-2">${notice}</li>`).join("");
      const result = await Swal.fire({
        icon: "info",
        title: "Avisos importantes",
        html:
          `<ul class="text-start ps-3 mb-3">${items}</ul>` +
          "<p class=\"mb-0\">¿El cliente está de acuerdo y desea continuar con la cotización?</p>",
        showCancelButton: true,
        confirmButtonText: "Sí, está de acuerdo",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      return result.isConfirmed;
    },
    async loadServiceSuggestions() {
      try {
        const response = await serviceOrdersService.getServiceSuggestions();
        this.serviceSuggestions = Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading service suggestions:", error);
        this.serviceSuggestions = [];
      }
    },
    filteredServiceSuggestions(term) {
      const query = String(term || "").trim().toLowerCase();
      return this.serviceSuggestions
        .filter((s) => {
          const name = String(s.name || "").toLowerCase();
          return (!query || name.includes(query)) && name !== query;
        })
        .slice(0, 8);
    },
    selectServiceSuggestion(service, suggestion) {
      service.name = suggestion.name;
      if (!service.price) {
        service.price = parseFloat(suggestion.price) || 0;
      }
      this.activeServiceIndex = null;
    },
    hideServiceSuggestionsDelayed() {
      clearTimeout(this.serviceSuggestionsTimer);
      this.serviceSuggestionsTimer = setTimeout(() => {
        this.activeServiceIndex = null;
      }, 150);
    },
    addService() {
      this.form.services.push(makeServiceRow());
    },
    removeService(index) {
      this.form.services.splice(index, 1);
    },
    formatMoney(value) {
      return (parseFloat(value) || 0).toLocaleString("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    async loadRegisteredBrands() {
      try {
        const response = await serviceOrdersService.getMotorcycleCatalog();
        this.registeredBrands = response?.data?.brands || [];
      } catch (error) {
        console.error("Error loading motorcycle brands:", error);
        this.registeredBrands = [];
      }
    },
    async loadRegisteredModelsForBrand() {
      const brand = (this.form.motorcycle_brand || "").trim();
      if (!brand) {
        this.registeredModels = [];
        return;
      }
      try {
        const response = await serviceOrdersService.getMotorcycleCatalog(brand);
        this.registeredModels = response?.data?.models || [];
      } catch (error) {
        console.error("Error loading motorcycle models:", error);
        this.registeredModels = [];
      }
    },
    onBrandInput() {
      this.showBrandSuggestions = true;
    },
    onModelInput() {
      this.showModelSuggestions = true;
    },
    selectBrandSuggestion(brand) {
      this.form.motorcycle_brand = brand;
      this.showBrandSuggestions = false;
      this.loadRegisteredModelsForBrand();
    },
    selectModelSuggestion(model) {
      this.form.motorcycle_model = model;
      this.showModelSuggestions = false;
    },
    hideBrandSuggestionsDelayed() {
      this.brandSuggestionsTimer = setTimeout(() => {
        this.showBrandSuggestions = false;
      }, 150);
    },
    hideModelSuggestionsDelayed() {
      this.modelSuggestionsTimer = setTimeout(() => {
        this.showModelSuggestions = false;
      }, 150);
    },
    formatTechnicianLabel(tech) {
      const name = (tech?.name || "").trim() || "Sin nombre";
      const doc = (tech?.document || "").trim();
      return doc ? `${name} - ${doc}` : name;
    },
    syncTechnicianFieldsFromUserId() {
      const tech = this.technicians.find(
        (t) => String(t.id) === String(this.form.user_id)
      );
      this.form.assigned_technician = tech?.name || "";
    },
    initTechnicianSelect2() {
      if (!this.$refs.technicianSelect) return;
      const select = $(this.$refs.technicianSelect);
      if (select.hasClass("select2-hidden-accessible")) {
        select.select2("destroy");
      }
      select.off("change");
      select.select2({
        placeholder: "Seleccione técnico",
        allowClear: true,
        width: "100%",
      });
      select.on("change", (e) => {
        this.form.user_id = e.target.value || "";
        this.syncTechnicianFieldsFromUserId();
      });
    },
    selectTechnicianInSelect2(userId) {
      if (!this.$refs.technicianSelect) return;
      const select = $(this.$refs.technicianSelect);
      if (!select.hasClass("select2-hidden-accessible")) {
        this.initTechnicianSelect2();
      }
      let value = userId ? String(userId) : "";
      if (!value && this.form.assigned_technician) {
        const byName = this.technicians.find(
          (t) => t.name === this.form.assigned_technician
        );
        if (byName) {
          value = String(byName.id);
          this.form.user_id = value;
          this.syncTechnicianFieldsFromUserId();
        }
      }
      if (!value) return;
      if (!select.find(`option[value="${value}"]`).length) return;
      select.val(value).trigger("change");
    },
    selectClient() {
      const selectedClient = this.clients.find((c) => String(c.id) === String(this.form.customer_id));
      if (!selectedClient) {
        this.form.customer_name = "";
        this.form.phone = "";
        return;
      }
      this.form.customer_name = selectedClient.name || "";
      this.form.phone = selectedClient.phone || "";
    },
    resolveClientPhone() {
      const direct = String(this.form.phone || '').trim();
      if (direct) return direct;
      const client = this.clients.find((c) => String(c.id) === String(this.form.customer_id));
      return client?.phone?.trim() || '';
    },
    openAddClientModal() {
      this.newClient = {
        name: "",
        document: "",
        phone: "",
        email: "",
        oil_reminder_enabled: false,
        oil_reminder_days: 30,
      };
      if (!this.addClientModal) {
        const modalElement = document.getElementById("ModalAddClientServiceOrder");
        this.addClientModal = new Modal(modalElement);
      }
      this.addClientModal.show();
    },
    closeAddClientModal() {
      if (this.addClientModal) {
        this.addClientModal.hide();
      }
    },
    async createClient() {
      if (!this.newClient.name?.trim()) {
        Swal.fire("Atención", "El nombre del cliente es obligatorio.", "warning");
        return;
      }
      try {
        const payload = {
          id: 0,
          name: this.newClient.name,
          email: this.newClient.email || `${Date.now()}@cliente.local`,
          password: "12345678",
          password_confirmation: "12345678",
          role: "4",
          status: "1",
          phone: this.newClient.phone || "",
          payment: "0",
          processes: [],
          address: "",
          document_type: "CC",
          document: this.newClient.document || "",
          oil_reminder_enabled: Boolean(this.newClient.oil_reminder_enabled),
          oil_reminder_days: this.newClient.oil_reminder_enabled
            ? (parseInt(this.newClient.oil_reminder_days, 10) || 30)
            : 30,
        };
        const response = await userService.createUser(payload);
        if (response && response.status >= 200 && response.status < 300) {
          await this.getClients();
          this.form.customer_id = String(response.data.id);
          this.$nextTick(() => {
            if (this.$refs.clientSelect) {
              $(this.$refs.clientSelect).val(this.form.customer_id).trigger("change");
            }
          });
          this.selectClient();
          this.closeAddClientModal();
          Swal.fire("Éxito", "Cliente creado correctamente", "success");
        }
      } catch (error) {
        const msg = error.response?.data?.message || "No se pudo crear el cliente";
        Swal.fire("Error", msg, "error");
      }
    },
    photoPreview(photo) {
      if (!photo) return "";
      if (typeof photo === "string") {
        return this.mediaUrl(photo);
      }
      if (photo.preview) {
        return photo.preview;
      }
      return this.mediaUrl(photo.file_path);
    },
    async uploadMotorcyclePhotoFile(orderId, file) {
      const formData = new FormData();
      formData.append("mediaable_id", orderId);
      formData.append("mediaable_type", MOTO_PHOTO_MEDIA_TYPE);
      formData.append("file", file);

      const response = await this.$store.dispatch("media/save", formData);
      if (!response || response.status < 200 || response.status >= 300) {
        throw new Error(response?.data?.message || "Error al subir la foto");
      }

      await this.clearMotorcyclePhotoPathInDatabase(orderId);

      return response.data;
    },
    async clearMotorcyclePhotoPathInDatabase(orderId = this.orderId) {
      if (!orderId) return;
      try {
        await serviceOrdersService.updateData(orderId, { motorcycle_photo_path: null });
        delete this.form.motorcycle_photo_path;
      } catch (error) {
        console.error("Error clearing legacy motorcycle_photo_path:", error);
      }
    },
    async uploadPendingMotorcyclePhotos(orderId) {
      const pendingPhotos = this.motorcyclePhotos.filter(
        (photo) => photo.pending && photo.file
      );
      if (!orderId || !pendingPhotos.length) {
        return;
      }

      for (const photo of pendingPhotos) {
        if (photo.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(photo.preview);
        }
        await this.uploadMotorcyclePhotoFile(orderId, photo.file);
      }

      this.pendingMotorcyclePhotos = [];
      await this.getMotorcyclePhotos();
    },
    parseLegacyMotorcyclePhotos(raw) {
      if (!raw || typeof raw !== "string" || !raw.includes("data:image")) {
        return [];
      }

      try {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : [parsed];
        return list.filter(
          (src) => typeof src === "string" && src.startsWith("data:image")
        );
      } catch {
        return raw.startsWith("data:image") ? [raw] : [];
      }
    },
    mergeLegacyMotorcyclePhotos(raw) {
      const legacyPhotos = this.parseLegacyMotorcyclePhotos(raw);
      legacyPhotos.forEach((src) => {
        const alreadyListed = this.motorcyclePhotos.some(
          (photo) => photo.preview === src
        );
        if (!alreadyListed) {
          this.motorcyclePhotos.push({
            id: null,
            preview: src,
            legacy: true,
          });
        }
      });
    },
    async getMotorcyclePhotos(legacyPhotoPath = null) {
      if (!this.orderId) {
        this.motorcyclePhotos = [];
        return;
      }

      try {
        const response = await this.$store.dispatch("media/getData", this.orderId);
        const payload = response?.data;
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);

        this.motorcyclePhotos = items
          .filter((item) => item.mediaable_type === MOTO_PHOTO_MEDIA_TYPE)
          .map((item) => ({
            id: item.id,
            file_path: item.file_path,
          }));
      } catch (error) {
        console.error("Error loading motorcycle photos:", error);
        this.motorcyclePhotos = [];
      }

      if (legacyPhotoPath) {
        this.mergeLegacyMotorcyclePhotos(legacyPhotoPath);
      }
    },
    async onPhotoChange(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (!files.length) return;

      this.uploadingMotorcyclePhoto = true;

      try {
        for (const file of files) {
          if (!file.type?.startsWith("image/")) {
            Swal.fire("Archivo no válido", "Solo se permiten imágenes.", "warning");
            continue;
          }

          let compressedFile;
          try {
            const dataUrl = await compressImageFile(file);
            compressedFile = dataUrlToFile(dataUrl, `moto_${Date.now()}.jpg`);
          } catch (error) {
            console.error("Error compressing photo:", error);
            Swal.fire(
              "Foto muy pesada",
              "No se pudo comprimir la imagen por debajo de 1 MB. Intente con otra foto.",
              "warning"
            );
            continue;
          }

          if (this.orderId) {
            await this.uploadMotorcyclePhotoFile(this.orderId, compressedFile);
            await this.getMotorcyclePhotos();
          } else {
            const preview = URL.createObjectURL(compressedFile);
            this.pendingMotorcyclePhotos.push(compressedFile);
            this.motorcyclePhotos.push({
              id: null,
              file_path: null,
              preview,
              pending: true,
              file: compressedFile,
            });
          }
        }
      } catch (error) {
        console.error("Error uploading motorcycle photo:", error);
        Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al subir la foto",
          "error"
        );
      } finally {
        this.uploadingMotorcyclePhoto = false;
      }
    },
    async removePhoto(index) {
      const photo = this.motorcyclePhotos[index];
      if (!photo) return;

      try {
        if (photo.id) {
          const response = await this.$store.dispatch("media/delete", photo.id);
          if (!response || response.status < 200 || response.status >= 300) {
            throw new Error(response?.data?.message || "Error al eliminar la foto");
          }
        } else if (photo.pending && photo.file) {
          const pendingIndex = this.pendingMotorcyclePhotos.indexOf(photo.file);
          if (pendingIndex !== -1) {
            this.pendingMotorcyclePhotos.splice(pendingIndex, 1);
          }
        }

        if (photo.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(photo.preview);
        }

        this.motorcyclePhotos.splice(index, 1);
      } catch (error) {
        console.error("Error deleting motorcycle photo:", error);
        Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo eliminar la foto",
          "error"
        );
      }
    },
    mediaUrl(filePath) {
      if (!filePath) return "";
      if (filePath.startsWith("http") || filePath.startsWith("data:")) return filePath;
      const base = String(this.apiUrl || "").replace(/\/$/, "");
      const path = String(filePath).replace(/^\/+/, "").replace(/\\/g, "/");
      return `${base}/${path}`;
    },
    async getEvidenceVideos() {
      if (!this.orderId) {
        this.evidenceVideos = [];
        return;
      }

      try {
        const response = await this.$store.dispatch('media/getData', this.orderId);
        this.evidenceVideos = (response.data || []).filter(
          (item) => item.mediaable_type === 'service_order_evidence_video'
        );
      } catch (error) {
        console.error('Error fetching evidence videos:', error);
        this.evidenceVideos = [];
      }
    },
    async onEvidenceVideoChange(event) {
      const fileInput = event?.target || this.$refs.evidenceVideoInput;
      const file = fileInput?.files?.[0];
      if (!file) return;

      if (!this.orderId) {
        Swal.fire('Advertencia', 'Debe guardar la orden antes de adjuntar el video.', 'warning');
        if (fileInput) fileInput.value = '';
        return;
      }

      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire('Error', 'El video no puede superar 50 MB.', 'error');
        if (fileInput) fileInput.value = '';
        return;
      }

      this.uploadingEvidenceVideo = true;
      try {
        const formData = new FormData();
        formData.append('mediaable_id', this.orderId);
        formData.append('mediaable_type', 'service_order_evidence_video');
        formData.append('file', file);

        const response = await this.$store.dispatch('media/save', formData);
        if (!response || response.status < 200 || response.status >= 300) {
          throw new Error(response?.data?.message || 'Error al subir el video');
        }

        await this.getEvidenceVideos();
        Swal.fire('Éxito', 'Video de evidencia adjuntado correctamente.', 'success');
      } catch (error) {
        console.error('Error uploading evidence video:', error);
        Swal.fire(
          'Error',
          error?.response?.data?.message || error.message || 'Error al subir el video',
          'error'
        );
      } finally {
        this.uploadingEvidenceVideo = false;
        if (fileInput) fileInput.value = '';
      }
    },
    async removeEvidenceVideo(videoId) {
      try {
        const result = await Swal.fire({
          title: '¿Eliminar video?',
          text: 'Esta acción no se puede deshacer.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar',
        });

        if (!result.isConfirmed) return;

        const response = await this.$store.dispatch('media/delete', videoId);
        if (response && response.status >= 200 && response.status < 300) {
          await this.getEvidenceVideos();
          Swal.fire('Eliminado', 'El video fue eliminado correctamente.', 'success');
        } else {
          throw new Error(response?.data?.message || 'Error al eliminar el video');
        }
      } catch (error) {
        console.error('Error deleting evidence video:', error);
        Swal.fire(
          'Error',
          error?.response?.data?.message || error.message || 'Error al eliminar el video',
          'error'
        );
      }
    },
    async generatePdf() {
      if (!this.orderId) return;
      try {
        const response = await serviceOrdersService.getDoc(this.orderId);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `OrdenServicio_${this.orderId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al generar PDF:", error);
        Swal.fire("Error", "No se pudo generar el PDF.", "error");
      }
    },
    async sharePublicPdf() {
      if (!this.orderId) {
        Swal.fire("Advertencia", "Guarde la orden antes de compartir el PDF.", "warning");
        return;
      }
      try {
        const response = await serviceOrdersService.getPublicPdfLink(this.orderId);
        const publicUrl = response?.data?.url;
        if (!publicUrl) throw new Error("No se recibió URL pública");

        const settingsRes = await this.$store.dispatch("settings/get");
        const companyName = settingsRes?.data?.company_name || this.$store.state.settings?.data?.company_name || "";
        const orderNo = String(this.orderId).padStart(4, "0");

        await sharePublicDocument({
          phone: this.resolveClientPhone(),
          companyName,
          detailText: `le comparto su orden de servicio N° ${orderNo}:\n${publicUrl}`,
          shareTitle: `Orden de Servicio N° ${orderNo}`,
          publicUrl,
          Swal,
        });
      } catch (error) {
        console.error("Error al compartir PDF:", error);
        const msg = error?.data?.message || error?.response?.data?.message || "No se pudo generar el enlace público.";
        Swal.fire("Error", msg, "error");
      }
    },
    async loadOrder() {
      try {
        const res = await this.getData(this.orderId);
        const data = res.data;
        this.form = { ...emptyForm(), ...data };
        this.form.status = Number(data.status ?? 0);
        this.form.services = (data.services || []).map((s) =>
          makeServiceRow(s.name || "", s.price != null ? parseFloat(s.price) : null)
        );
        this.form.customer_id = data.client_id ? String(data.client_id) : "";
        this.form.user_id = data.user_id ? String(data.user_id) : "";
        if (data.entry_at) {
          const d = new Date(data.entry_at);
          d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
          this.form.entry_at = d.toISOString().slice(0, 16);
        }
        const legacyPhotoPath = data.motorcycle_photo_path || null;
        await this.getMotorcyclePhotos(legacyPhotoPath);
        delete this.form.motorcycle_photo_path;
        await this.getEvidenceVideos();
        if (this.form.customer_id && !this.form.phone) {
          this.selectClient();
        }
        if (data.user) {
          this.ensureTechnicianInList(data.user);
        } else if (data.user_id) {
          this.ensureTechnicianInList({
            id: data.user_id,
            name: data.assigned_technician,
          });
        }
        this.syncTechnicianFieldsFromUserId();
        await this.loadRegisteredModelsForBrand();
        await this.$nextTick();
        this.$nextTick(() => {
          this.initCanvas();
          this.initClientSelect2();
          this.initTechnicianSelect2();
          this.$nextTick(() => {
            this.selectTechnicianInSelect2(this.form.user_id);
          });
          if (data.customer_signature) {
            const img = new Image();
            img.onload = () => {
              const canvas = this.$refs.signatureCanvas;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            };
            img.src = data.customer_signature;
          }
        });
      } catch {
        Swal.fire("Error", "No se pudo cargar la orden.", "error");
        this.$router.push("/service-orders");
      }
    },
    buildPayload() {
      const canvas = this.$refs.signatureCanvas;
      const signature = canvas.toDataURL("image/png");
      const hasInk = signature && signature.length > 100;
      this.syncTechnicianFieldsFromUserId();
      const formData = { ...this.form };
      delete formData.customer_id;

      return {
        ...formData,
        status: Number(this.form.status ?? 0),
        user_id: this.form.user_id ? parseInt(this.form.user_id, 10) : null,
        client_id: this.form.customer_id || null,
        customer_signature: hasInk ? signature : this.form.customer_signature,
        entry_at: this.form.entry_at || null,
        motorcycle_photo_path: null,
        services: this.form.services
          .filter((s) => String(s.name || "").trim())
          .map((s) => ({ name: String(s.name).trim(), price: parseFloat(s.price) || 0 })),
        id: this.orderId || undefined,
      };
    },
    statusSaveMessage() {
      const status = Number(this.form.status ?? 0);
      if (status === 1) return "Orden formalizada correctamente.";
      if (status === 2) return "Orden marcada como rechazada.";
      return "Borrador guardado correctamente.";
    },
    async submitForm() {
      if (!this.form.customer_id) {
        Swal.fire("Atención", "Debe seleccionar un cliente.", "warning");
        return;
      }
      if (!this.form.user_id) {
        Swal.fire("Atención", "Debe seleccionar un técnico asignado.", "warning");
        return;
      }
      this.saving = true;
      try {
        const payload = this.buildPayload();
        let response;
        if (this.isEdit) {
          response = await this.save({ ...payload, id: this.orderId });
        } else {
          response = await this.save(payload);
          const newId = response?.data?.id;
          if (newId) {
            await this.uploadPendingMotorcyclePhotos(newId);
          }
        }
        Swal.fire("Guardado", this.statusSaveMessage(), "success");
        this.$router.push("/service-orders");
      } catch (err) {
        const msg = err?.response?.data?.message || "No se pudo guardar la orden.";
        Swal.fire("Error", msg, "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.select2-container {
  width: 100% !important;
}

.select2-container--default .select2-selection--single {
  height: 31.5px !important;
  border: 1px solid #d2d6da !important;
  border-radius: 0.375rem !important;
}

.select2-container--default .select2-selection--single .select2-selection__rendered {
  line-height: 29.5px !important;
  padding-left: 0.5rem !important;
  padding-right: 20px !important;
}

.select2-container--default .select2-selection--single .select2-selection__arrow {
  height: 29.5px !important;
  right: 8px !important;
  top: 1px !important;
}

.brand-suggestions {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 180px;
  overflow-y: auto;
  margin-top: 4px;
  border: 1px solid #d2d6da;
  border-radius: 0.375rem;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.brand-suggestion-item {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #344767;
}

.brand-suggestion-item:hover {
  background: #f6f9fc;
}

.photo-card {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.photo-remove-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: #ef476f;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  padding: 0;
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.photo-remove-btn:hover {
  background: #d90429;
  transform: scale(1.06);
}

.inventory-radio-wrap {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 1.25rem;
  padding-left: 0;
}

.inventory-radio-wrap .inventory-radio-input[type="radio"] {
  float: none;
  margin: 0;
  width: 1.05rem;
  height: 1.05rem;
  padding: 0 !important;
  border: 2px solid #344767 !important;
  border-radius: 50%;
  background-color: #fff;
  background-image: none;
  cursor: pointer;
}

.inventory-radio-wrap .inventory-radio-input[type="radio"]::after {
  content: none !important;
  display: none !important;
}

.inventory-radio-wrap .inventory-radio-input[type="radio"]:checked {
  border: 2px solid #344767 !important;
  padding: 0 !important;
  background-color: #344767;
  background-image: radial-gradient(circle, #fff 0.2rem, #344767 0.21rem);
  background-position: center center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.inventory-radio-wrap .inventory-radio-input[type="radio"]:focus {
  box-shadow: 0 0 0 0.15rem rgba(52, 71, 103, 0.25);
}

.doc-check-wrap {
  min-height: 31.5px;
}

.doc-check-input {
  appearance: none;
  -webkit-appearance: none;
  width: 1.05rem;
  height: 1.05rem;
  margin-top: 0;
  border: 2px solid #344767;
  border-radius: 0.25rem;
  background-color: #fff;
  cursor: pointer;
  display: inline-grid;
  place-content: center;
}

.doc-check-input::after {
  content: "";
  width: 0.62rem;
  height: 0.62rem;
  transform: scale(0);
  transition: transform 0.12s ease-in-out;
  transform-origin: center;
  clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  background-color: #ffffff;
}

.doc-check-input:checked {
  background-color: #344767;
  border-color: #344767;
}

.doc-check-input:checked::after {
  transform: scale(1);
}

.doc-check-label {
  color: #344767;
  font-weight: 500;
  cursor: pointer;
}

.evidence-video-card {
  max-width: 480px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.evidence-video-player {
  width: 100%;
  max-height: 280px;
  border-radius: 8px;
  background: #000;
  display: block;
}

.observations-voice-wrap {
  width: 100%;
}

.observations-textarea {
  padding-right: 2.75rem;
  padding-bottom: 2.5rem;
  resize: vertical;
}

.observations-voice-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #f8f9fa;
  color: #344767;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.observations-voice-btn:hover {
  background: #e9ecef;
  transform: scale(1.05);
}

.observations-voice-btn--active {
  background: #f5365c;
  color: #fff;
  animation: observations-voice-pulse 1.2s ease-in-out infinite;
}

@keyframes observations-voice-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 54, 92, 0.45);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(245, 54, 92, 0);
  }
}
</style>

